/**
 * NavigationSettingsPanel - UI for configuring DirectHitStrategy filters
 *
 * Features:
 * - Distance threshold slider (% of frame width)
 * - 3D distance filter toggle + threshold
 * - Delta angle filter toggle + threshold
 * - Connectivity filter toggle
 * - Fallback strategy radio buttons
 * - Max candidates limit
 * - Reset to defaults button
 * - Display current selection info
 *
 * Integration:
 * - Uses useNavigationSettings hook for state
 * - Auto-saves to localStorage on changes
 * - No parameters needed - connects directly to store
 */

import React, { useCallback } from 'react'
import {
  DEFAULT_NAVIGATION_SETTINGS,
  NavigationSettings,
  SETTINGS_LABELS,
  SETTINGS_CONSTRAINTS,
} from '../../types/NavigationSettings'
import { useNavigationSettings } from '../../hooks/useNavigationSettings'

export interface NavigationSettingsPanelProps {
  onSettingsChange?: (settings: NavigationSettings) => void
  compact?: boolean // If true, show minimal version
}

/**
 * Main settings panel component
 */
export const NavigationSettingsPanel: React.FC<NavigationSettingsPanelProps> = ({
  onSettingsChange,
  compact = false,
}) => {
  const {
    settings,
    updateSetting,
    reset,
    error,
    isLoading,
  } = useNavigationSettings()

  const handleSliderChange = useCallback(
    (key: keyof NavigationSettings, value: any) => {
      // Calculate new settings immediately (don't wait for store update)
      const newSettings = { ...settings, [key]: value }
      updateSetting(key, value)
      onSettingsChange?.(newSettings)  // ✅ Pass new settings immediately
    },
    [settings, updateSetting, onSettingsChange]
  )

  const handleToggle = useCallback(
    (key: keyof NavigationSettings) => {
      const currentValue = settings[key]
      const newValue = typeof currentValue === 'boolean' ? !currentValue : currentValue
      // Calculate new settings immediately
      const newSettings = { ...settings, [key]: newValue }
      updateSetting(key, newValue)
      onSettingsChange?.(newSettings)  // ✅ Pass new settings immediately
    },
    [settings, updateSetting, onSettingsChange]
  )

  const handleReset = useCallback(() => {
    reset()
    onSettingsChange?.(DEFAULT_NAVIGATION_SETTINGS)  // ✅ Pass reset settings immediately
  }, [reset, onSettingsChange])

  if (compact) {
    return (
      <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3 text-sm">
        {/* Error display */}
        {error && (
          <div className="rounded bg-red-50 p-2 text-red-700">
            <p className="text-xs font-medium">⚠️ {error}</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="font-medium text-gray-700">Navigation Settings</span>
          <div className="flex items-center gap-2">
            {isLoading && <span className="text-xs text-gray-500">Saving...</span>}
            <button
              onClick={handleReset}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Reset
            </button>
          </div>
        </div>
        <SliderControl
          label="Distance Threshold"
          value={settings.distanceThresholdPercent}
          min={SETTINGS_CONSTRAINTS.distanceThresholdPercent.min}
          max={SETTINGS_CONSTRAINTS.distanceThresholdPercent.max}
          step={1}
          unit="%"
          onChange={(val) => handleSliderChange('distanceThresholdPercent', val)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-lg border-2 p-3" style={{ borderColor: '#4CAF50', backgroundColor: 'rgba(30, 30, 30, 0.02)' }}>
      {/* Error display */}
      {error && (
        <div className="rounded bg-red-50 p-3 text-red-700">
          <p className="font-medium text-sm">⚠️ Settings Error</p>
          <p className="text-xs mt-1">{error}</p>
          <button
            onClick={() => reset()}
            className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
          >
            Reset to defaults
          </button>
        </div>
      )}
      <div className="flex items-center justify-end pb-2" style={{ borderBottomColor: '#4CAF50', borderBottomWidth: '1px' }}>
        <div className="flex items-center gap-2">
          {isLoading && <span className="text-xs text-gray-500 animate-pulse">Saving...</span>}
          <button
            onClick={handleReset}
            className="rounded px-2 py-1 text-xs transition-colors"
            style={{ backgroundColor: '#4CAF50', color: 'white', fontWeight: '500' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Use Progressive Weakening & Hysteresis */}
      <ToggleControl
        label={SETTINGS_LABELS.useProgressiveWeakeningAndHysteresis}
        checked={settings.useProgressiveWeakeningAndHysteresis}
        onChange={() => handleToggle('useProgressiveWeakeningAndHysteresis')}
        helpText="Enable progressive shrinking of sticky bounds after failed pans and hysteresis to prevent oscillation during frame selection"
      />

      {/* Sticky Frame Selection */}
      <ToggleControl
        label={SETTINGS_LABELS.useStickyFrameSelection}
        checked={settings.useStickyFrameSelection}
        onChange={() => handleToggle('useStickyFrameSelection')}
        helpText="Keep selected frame until red dot exits its bounds, then select nearest frame"
      />

      {/* Sticky Bounds Ratio */}
      {settings.useStickyFrameSelection && (
        <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
          <SliderControl
            label={SETTINGS_LABELS.stickyRatio}
            value={settings.stickyRatio}
            min={SETTINGS_CONSTRAINTS.stickyRatio.min}
            max={SETTINGS_CONSTRAINTS.stickyRatio.max}
            step={0.1}
            unit="x"
            onChange={(val) => handleSliderChange('stickyRatio', val)}
            helpText="Circular tolerance radius as ratio of minimum frame dimension (0.5 = half, 1.0 = full, 2.0 = double). Equal in all directions."
          />
        </div>
      )}

      {/* Enable Pan Selection */}
      <ToggleControl
        label={SETTINGS_LABELS.enablePanSelection}
        checked={settings.enablePanSelection}
        onChange={() => handleToggle('enablePanSelection')}
        helpText="Allow frame selection during pan movements, not just at pan end"
      />

      {/* Enable Directional Pan Selection */}
      <ToggleControl
        label={SETTINGS_LABELS.enableDirectionalPanSelection}
        checked={settings.enableDirectionalPanSelection}
        onChange={() => handleToggle('enableDirectionalPanSelection')}
        helpText="Allow selecting adjacent frames (n±1, n±2, n±3) based on pan motion direction, even within sticky bounds. Enables smooth sequential navigation."
      />

      {/* Show Frame Boundaries and Labels */}
      <ToggleControl
        label={SETTINGS_LABELS.showFrameBoundariesAndLabels}
        checked={settings.showFrameBoundariesAndLabels}
        onChange={() => handleToggle('showFrameBoundariesAndLabels')}
        helpText="Display frame borders and index labels on the canvas"
      />

      {/* Show Extra Context Frames */}
      <ToggleControl
        label={SETTINGS_LABELS.showExtraContextFrames}
        checked={settings.showExtraContextFrames}
        onChange={() => handleToggle('showExtraContextFrames')}
        helpText="Show additional context frames when viewport is large (zoomed out) for spatial awareness"
      />

      {/* Enable Auto-Return Animation */}
      <ToggleControl
        label={SETTINGS_LABELS.enableAutoReturn}
        checked={settings.enableAutoReturn}
        onChange={() => handleToggle('enableAutoReturn')}
        helpText="Automatically animate viewport to center selected frame under red dot after pan ends"
      />

      {/* Animation Settings Section */}
      <div className="space-y-2 rounded p-3" style={{ backgroundColor: 'rgba(255, 150, 100, 0.08)', borderLeft: '4px solid #ff9564' }}>
        <div className="flex items-center gap-0 pb-2" style={{ borderBottomColor: '#ff9564', borderBottomWidth: '1px' }}>
          <label className="text-xs font-bold" style={{ color: '#ff9564' }}>
            🎬 Frame Animation
          </label>
        </div>
        
        {/* Pan-Move Preview Animation */}
        <ToggleControl
          label={SETTINGS_LABELS.enablePanMoveAnimation}
          checked={settings.enablePanMoveAnimation}
          onChange={() => handleToggle('enablePanMoveAnimation')}
          helpText="Show yellow preview animation during pan movements when frame changes (faster, semi-transparent)"
        />

        {/* Pan-End Smooth Animation */}
        <ToggleControl
          label={SETTINGS_LABELS.enablePanEndAnimation}
          checked={settings.enablePanEndAnimation}
          onChange={() => handleToggle('enablePanEndAnimation')}
          helpText="Show smooth magenta animation when pan ends and jumping more than 1 frame (slower, full opacity)"
        />
      </div>

      {/* Distance Threshold */}
      <SliderControl
        label={SETTINGS_LABELS.distanceThresholdPercent}
        value={settings.distanceThresholdPercent}
        min={SETTINGS_CONSTRAINTS.distanceThresholdPercent.min}
        max={SETTINGS_CONSTRAINTS.distanceThresholdPercent.max}
        step={1}
        unit="%"
        onChange={(val) => handleSliderChange('distanceThresholdPercent', val)}
        helpText="Percentage of frame width for auto-selection snap distance"
      />

      {/* Connectivity Filter */}
      <ToggleControl
        label={SETTINGS_LABELS.useConnectivityFiltering}
        checked={settings.useConnectivityFiltering}
        onChange={() => handleToggle('useConnectivityFiltering')}
        helpText="Only select frames connected in the connectivity graph"
      />

      {/* 3D Distance Filter */}
      <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
        <ToggleControl
          label={SETTINGS_LABELS.use3DDistanceFilter}
          checked={settings.use3DDistanceFilter}
          onChange={() => handleToggle('use3DDistanceFilter')}
          helpText="Filter frames by 3D distance from current frame"
        />
        {settings.use3DDistanceFilter && (
          <SliderControl
            label={SETTINGS_LABELS.max3DDistance}
            value={settings.max3DDistance}
            min={SETTINGS_CONSTRAINTS.max3DDistance.min}
            max={SETTINGS_CONSTRAINTS.max3DDistance.max}
            step={0.5}
            unit="units"
            onChange={(val) => handleSliderChange('max3DDistance', val)}
            indented
          />
        )}
      </div>

      {/* Delta Angle Filter */}
      <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
        <ToggleControl
          label={SETTINGS_LABELS.useDeltaAngleFilter}
          checked={settings.useDeltaAngleFilter}
          onChange={() => handleToggle('useDeltaAngleFilter')}
          helpText="Filter frames by maximum angle difference from current frame"
        />
        {settings.useDeltaAngleFilter && (
          <SliderControl
            label={SETTINGS_LABELS.maxDeltaAngle}
            value={settings.maxDeltaAngle}
            min={SETTINGS_CONSTRAINTS.maxDeltaAngle.min}
            max={SETTINGS_CONSTRAINTS.maxDeltaAngle.max}
            step={1}
            unit="degrees"
            onChange={(val) => handleSliderChange('maxDeltaAngle', val)}
            indented
          />
        )}
      </div>

      {/* Max Candidates */}
      <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
        <ToggleControl
          label={SETTINGS_LABELS.maxCandidates}
          checked={settings.maxCandidates === null}
          onChange={() => handleSliderChange('maxCandidates', settings.maxCandidates === null ? 50 : null)}
          helpText="Enable to allow unlimited candidates, disable to set a limit"
        />
        {settings.maxCandidates !== null && (
          <SliderControl
            label="Candidate Limit"
            value={settings.maxCandidates}
            min={SETTINGS_CONSTRAINTS.maxCandidates.min}
            max={200}
            step={5}
            unit="frames"
            onChange={(val) => handleSliderChange('maxCandidates', val)}
            indented
          />
        )}
      </div>

      {/* Max Zoom Delta (dZ) */}
      <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
        <SliderControl
          label={SETTINGS_LABELS.maxDz}
          value={settings.maxDz}
          min={SETTINGS_CONSTRAINTS.maxDz.min}
          max={SETTINGS_CONSTRAINTS.maxDz.max}
          step={0.1}
          unit="units"
          onChange={(val) => handleSliderChange('maxDz', val)}
          helpText="Maximum zoom level change (dZ) between current and next frame"
        />
      </div>

      {/* NEW: Universal Window-Based Filtering Section */}
      <div className="space-y-2 rounded p-3" style={{ backgroundColor: 'rgba(100, 150, 255, 0.08)', borderLeft: '4px solid #4589ff' }}>
        <div className="flex items-center gap-0 pb-2" style={{ borderBottomColor: '#4589ff', borderBottomWidth: '1px' }}>
          <label className="text-xs font-bold" style={{ color: '#4589ff' }}>
            📱 Temporal Window Filtering
          </label>
        </div>
        
        <SliderControl
          label={SETTINGS_LABELS.sequentialWindow}
          value={settings.sequentialWindow}
          min={SETTINGS_CONSTRAINTS.sequentialWindow.min}
          max={SETTINGS_CONSTRAINTS.sequentialWindow.max}
          step={1}
          unit="frames"
          onChange={(val) => handleSliderChange('sequentialWindow', val)}
          helpText="Sequential window: show frames within ±N frames of current frame"
        />

        <SliderControl
          label={SETTINGS_LABELS.nearWindow}
          value={settings.nearWindow}
          min={SETTINGS_CONSTRAINTS.nearWindow.min}
          max={SETTINGS_CONSTRAINTS.nearWindow.max}
          step={1}
          unit="frames"
          onChange={(val) => handleSliderChange('nearWindow', val)}
          helpText="Near window: extended reach for zigzag patterns (shelf scanning)"
        />

        <SliderControl
          label={SETTINGS_LABELS.contextualWindow}
          value={settings.contextualWindow}
          min={SETTINGS_CONSTRAINTS.contextualWindow.min}
          max={SETTINGS_CONSTRAINTS.contextualWindow.max}
          step={1}
          unit="frames"
          onChange={(val) => handleSliderChange('contextualWindow', val)}
          helpText="Contextual window: absolute maximum - no frames beyond this limit (applies to all shops)"
        />

        {/* Enforce Max Jump Distance (Sequential Mode Only) */}
        <div className="space-y-1 pt-2" style={{ borderTopColor: '#4589ff', borderTopWidth: '1px', marginTop: '8px' }}>
          <ToggleControl
            label={SETTINGS_LABELS.enforceMaxJumpDistance}
            checked={settings.enforceMaxJumpDistance}
            onChange={() => handleToggle('enforceMaxJumpDistance')}
            helpText={`Prevents large frame jumps during panning (auto-selection only). Max jump distance limited to nearWindow (±${settings.nearWindow} frames). Manual clicks/double-clicks are NOT restricted. Only applies to phase correlation datasets.`}
          />
        </div>
      </div>

      {/* Fallback Strategy */}
      <div className="space-y-2 rounded p-2" style={{ backgroundColor: 'rgba(76, 175, 80, 0.05)', borderLeft: '3px solid #4CAF50' }}>
        <div className="flex items-center gap-0">
          <label className="text-xs font-medium" style={{ color: '#7ee787' }}>
            {SETTINGS_LABELS.fallbackStrategy}
          </label>
          <InfoIcon helpText="Strategy for handling frames when filters are too strict: Less Restrictive gradually relaxes filters, Unrestricted 2D jumps to any nearby frame, None means no selection" />
        </div>
        <select
          value={settings.fallbackStrategy}
          onChange={(e) => handleSliderChange('fallbackStrategy', e.target.value as any)}
          className="w-full rounded border-2 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none transition-colors"
          style={{ borderColor: '#4CAF50' }}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#45a049')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#4CAF50')}
        >
          <option value="less-restrictive">Less Restrictive (Progressive Degradation)</option>
          <option value="unrestricted-2d">Unrestricted 2D</option>
          <option value="none">None (Strict Filters Only)</option>
        </select>
      </div>

      {/* Current Settings Summary */}
      <SettingsSummary settings={settings} />
    </div>
  )
}

/**
 * Slider control component
 */
interface SliderControlProps {
  label: string
  value: number
  min: number
  max: number
  step: number
  unit: string
  onChange: (value: number) => void
  helpText?: string
  indented?: boolean
}

const SliderControl: React.FC<SliderControlProps> = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  helpText,
  indented = false,
}) => (
  <div className={indented ? 'ml-6 space-y-1' : 'space-y-1'}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-0">
        <label className="text-xs font-medium" style={{ color: '#7ee787' }}>{label}</label>
        {helpText && <InfoIcon helpText={helpText} />}
      </div>
      <span className="text-xs font-semibold" style={{ color: '#4CAF50' }}>
        {value} {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full"
    />
  </div>
)/**
 * Toggle checkbox component
 */
interface ToggleControlProps {
  label: string
  checked: boolean
  onChange: () => void
  helpText?: string
  indented?: boolean
}

const ToggleControl: React.FC<ToggleControlProps> = ({
  label,
  checked,
  onChange,
  helpText,
  indented = false,
}) => (
  <div className={indented ? 'ml-6 space-y-1' : 'space-y-1'}>
    <label className="flex items-center space-x-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded accent-green-500"
        style={{ accentColor: '#4CAF50' }}
      />
      <span className="text-xs font-medium flex items-center gap-0" style={{ color: '#7ee787' }}>
        {label}
        {helpText && <InfoIcon helpText={helpText} />}
      </span>
    </label>
  </div>
)

/**
 * Radio button component
 */

/**
 * Info icon with help text modal - beautiful SVG icon
 */
interface InfoIconProps {
  helpText: string
}

const InfoIcon: React.FC<InfoIconProps> = ({ helpText }) => {
  const [showHelp, setShowHelp] = React.useState(false)

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setShowHelp(!showHelp)}
        className="inline-flex items-center justify-center rounded-full hover:bg-blue-50 transition-colors"
        aria-label="Show help"
        style={{ width: '18px', height: '18px' }}
      >
        <svg
          className="w-4 h-4 text-blue-500 hover:text-blue-600"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {showHelp && (
        <>
          {/* Backdrop to close on click */}
          <button
            type="button"
            onClick={() => setShowHelp(false)}
            className="fixed inset-0 z-40 cursor-default bg-transparent"
            aria-label="Close help"
          />
          {/* Help tooltip - match frame balloon look (dark, green border, shadow) */}
          <div className="absolute right-0 top-6 z-50 w-64 rounded-lg bg-[rgba(30,30,30,0.95)] p-3 text-xs text-white shadow-[0_4px_12px_rgba(0,0,0,0.5)] border-2 border-[#4CAF50]">
            <div className="text-gray-100 leading-relaxed">{helpText}</div>
            {/* Arrow pointing up (matches tooltip background) */}
            <div className="absolute -top-2 right-3 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-[rgba(30,30,30,0.95)]" />
          </div>
        </>
      )}
    </span>
  )
}

/**
 * Settings summary component
 */
interface SettingsSummaryProps {
  settings: NavigationSettings
}

const SettingsSummary: React.FC<SettingsSummaryProps> = ({ settings }) => {
  const activeFilters: string[] = []

  if (settings.useConnectivityFiltering) activeFilters.push('Connectivity')
  if (settings.use3DDistanceFilter) activeFilters.push('3D Distance')
  if (settings.useDeltaAngleFilter) activeFilters.push('Delta Angle')
  if (settings.maxCandidates !== null) activeFilters.push('Candidate Limit')

  return (
    <div className="rounded p-3" style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', borderLeft: '3px solid #4CAF50' }}>
      <p className="font-medium text-xs" style={{ color: '#4CAF50' }}>Active Filters:</p>
      {activeFilters.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {activeFilters.map((filter) => (
            <span
              key={filter}
              className="inline-block rounded px-2 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: '#4CAF50' }}
            >
              {filter}
            </span>
          ))}
        </div>
      ) : (
        <p className="mt-1 text-xs text-gray-500">No filters active</p>
      )}
      <p className="mt-2 text-xs text-gray-600">
        Fallback: <span className="font-medium" style={{ color: '#4CAF50' }}>{settings.fallbackStrategy}</span>
      </p>
    </div>
  )
}

export default NavigationSettingsPanel
